import React, { useState } from 'react';
import { Modal, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';

// E.164-friendly: optional leading "+", then 7-15 digits.
// We strip dashes/spaces/parentheses before validating.
const PHONE_REGEX = /^\+?\d{7,15}$/;

const sanitize = (raw) => (raw || '').replace(/[^\d+]/g, '');
const isPhoneValid = (raw) => PHONE_REGEX.test(sanitize(raw));

const WhatsAppModal = ({
    open,
    onClose,
    onSend,
    targetPhones,
    setTargetPhones,
    filteredData,
    selectionModel,
}) => {
    const { t } = useTranslation();
    const [draft, setDraft] = useState('');
    const [error, setError] = useState('');
    const [sendHover, setSendHover] = useState(false);
    const [cancelHover, setCancelHover] = useState(false);

    const phones = Array.isArray(targetPhones) ? targetPhones : [];

    const commitDraft = (value) => {
        const candidate = sanitize(value ?? draft).replace(/[,;]+$/, '');
        if (!candidate) return true;
        if (!isPhoneValid(candidate)) {
            setError(t('InvalidPhoneNumber'));
            return false;
        }
        if (phones.some((p) => p === candidate)) {
            setDraft('');
            setError('');
            return true;
        }
        setTargetPhones([...phones, candidate]);
        setDraft('');
        setError('');
        return true;
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ',' || e.key === ';' || e.key === ' ') {
            e.preventDefault();
            commitDraft();
        } else if (e.key === 'Backspace' && draft === '' && phones.length > 0) {
            setTargetPhones(phones.slice(0, -1));
        }
    };

    const handlePaste = (e) => {
        const text = e.clipboardData.getData('text');
        if (!text) return;
        if (/[,;\s]/.test(text)) {
            e.preventDefault();
            const parts = text.split(/[,;\s]+/).map((p) => sanitize(p)).filter(Boolean);
            const next = [...phones];
            const seen = new Set(next);
            let invalid = '';
            for (const p of parts) {
                if (!isPhoneValid(p)) {
                    invalid = p;
                    continue;
                }
                if (!seen.has(p)) {
                    next.push(p);
                    seen.add(p);
                }
            }
            setTargetPhones(next);
            setDraft('');
            setError(invalid ? `${t('InvalidPhoneNumber')}: ${invalid}` : '');
        }
    };

    const removePhone = (phone) => {
        setTargetPhones(phones.filter((p) => p !== phone));
    };

    const handleSend = async () => {
        if (!commitDraft()) return;
        const finalPhones = (() => {
            const candidate = sanitize(draft);
            if (!candidate) return phones;
            if (!isPhoneValid(candidate)) return phones;
            if (phones.some((p) => p === candidate)) return phones;
            return [...phones, candidate];
        })();

        if (finalPhones.length === 0) {
            setError(t('EnterPhoneNumber'));
            return;
        }

        onClose();
        await onSend(
            filteredData.filter((r) => selectionModel.includes(r.id)),
            finalPhones,
        );
    };

    const canSend = phones.length > 0 || isPhoneValid(draft);

    return (
        <Modal open={open} onClose={onClose}>
            <div
                style={{
                    background: '#fff',
                    borderRadius: '16px',
                    maxWidth: '480px',
                    width: '90%',
                    margin: '10% auto',
                    padding: '24px',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '16px',
                    fontFamily: 'Montserrat, sans-serif',
                }}
            >
                <h2
                    style={{
                        margin: 0,
                        fontSize: '20px',
                        color: '#25D366',
                        textAlign: 'center',
                    }}
                >
                    {t('EnterPhoneNumbers')}
                </h2>

                <div
                    style={{
                        border: '1px solid #d0e6d8',
                        borderRadius: '12px',
                        backgroundColor: '#f3fbf6',
                        padding: '8px',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '6px',
                        minHeight: '52px',
                    }}
                >
                    {phones.map((phone) => (
                        <span
                            key={phone}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                background: '#25D366',
                                color: '#fff',
                                padding: '4px 10px',
                                borderRadius: '999px',
                                fontSize: '13px',
                                maxWidth: '100%',
                                direction: 'ltr',
                            }}
                        >
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {phone}
                            </span>
                            <button
                                type="button"
                                onClick={() => removePhone(phone)}
                                style={{
                                    background: 'transparent',
                                    border: 'none',
                                    color: '#fff',
                                    cursor: 'pointer',
                                    fontWeight: 'bold',
                                    fontSize: '14px',
                                    lineHeight: 1,
                                }}
                                aria-label={`remove ${phone}`}
                            >
                                ×
                            </button>
                        </span>
                    ))}
                    <TextField
                        value={draft}
                        onChange={(e) => {
                            setDraft(e.target.value);
                            if (error) setError('');
                        }}
                        onKeyDown={handleKeyDown}
                        onBlur={() => commitDraft()}
                        onPaste={handlePaste}
                        placeholder={phones.length === 0 ? '0501234567' : ''}
                        variant="standard"
                        InputProps={{ disableUnderline: true }}
                        sx={{
                            flex: 1,
                            minWidth: '160px',
                            '& input': { padding: '6px 4px', fontSize: '14px', direction: 'ltr' },
                        }}
                    />
                </div>
                <span
                    style={{
                        minHeight: '18px',
                        fontSize: '12px',
                        color: error ? '#d32f2f' : '#666',
                    }}
                >
                    {error || t('PhoneChipHint')}
                </span>

                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px' }}>
                    <button
                        style={{
                            flex: 1,
                            padding: '12px',
                            borderRadius: '12px',
                            background: sendHover && canSend ? '#1ea957' : '#25D366',
                            color: '#fff',
                            border: 'none',
                            fontWeight: 'bold',
                            cursor: canSend ? 'pointer' : 'not-allowed',
                            opacity: canSend ? 1 : 0.5,
                            transition: 'background 0.3s ease',
                        }}
                        disabled={!canSend}
                        onMouseEnter={() => setSendHover(true)}
                        onMouseLeave={() => setSendHover(false)}
                        onClick={handleSend}
                    >
                        {t('Send')}
                    </button>

                    <button
                        style={{
                            flex: 1,
                            padding: '12px',
                            borderRadius: '12px',
                            background: cancelHover ? '#e0e0e0' : '#f0f0f0',
                            color: '#333',
                            border: 'none',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            transition: 'background 0.3s ease',
                        }}
                        onMouseEnter={() => setCancelHover(true)}
                        onMouseLeave={() => setCancelHover(false)}
                        onClick={() => {
                            setTargetPhones([]);
                            setDraft('');
                            setError('');
                            onClose();
                        }}
                    >
                        {t('Cancel')}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default WhatsAppModal;
